/**
 * Dettaglio gara: testata, invitati, invio, offerte.
 *
 * Cosa si può toccare lo decide lo stato, e lo decide il server: qui la pagina mostra solo i
 * comandi che il server accetterebbe, ma il controllo vero è nella route (`routes/acquisti/gare.js`).
 *
 *   Bozza           tutto: testata, RSA, invitati. «Invia» blocca il capitolato, archivia il PDF,
 *                   porta gli invitati a «In trattativa» e manda le email.
 *   Inviata         note e scadenza; invitati nuovi, inviati uno per uno; offerte.
 *   In valutazione  offerte (normalizzazione); niente invitati nuovi.
 *   Aggiudicata / Annullata   sola lettura.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@shared/context/AuthContext";
import { useToast } from "@shared/components/ui/Toast";
import { gareApi, fornitoriApi, anagraficheApi } from "../services/acquistiApi";
import { apriFile } from "../services/file";
import {
  Campo,
  Testo,
  AreaTesto,
  Data,
  Scelta,
  Caricamento,
  Avviso,
} from "../components/campi";
import {
  STATO_GARA,
  STATO_GARA_BADGE,
  ORIGINE_GARA,
  STATO_INVITO,
  STATO_INVITO_BADGE,
  STATO_OFFERTA,
  STATO_OFFERTA_BADGE,
  LIVELLO_RISERVATEZZA,
  LIVELLO_BADGE,
  GARA_BOZZA,
  GARA_INVIATA,
  GARA_IN_VALUTAZIONE,
  GARA_AGGIUDICATA,
  GARA_ANNULLATA,
  INVITO_DA_INVITARE,
  INVITO_FALLITO,
  INVITO_INVITATO,
  INVITO_HA_RISPOSTO,
  OFFERTA_SUPERATA,
  OFFERTA_RITIRATA,
} from "../constants/optionSet";
import { dataIt, dataInput, euro, etichetta } from "../utils/formato";

export default function GaraPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { canPerformAction } = useAuth();
  const puoScrivere = canPerformAction("gare.gestisci");

  const [gara, setGara] = useState(null);
  const [testata, setTestata] = useState({});
  const [rsaIds, setRsaIds] = useState([]);
  const [rsa, setRsa] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [nuovoFornitore, setNuovoFornitore] = useState("");
  const [email, setEmail] = useState({});
  const [caricamento, setCaricamento] = useState(true);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState(null);
  const [esitiInvio, setEsitiInvio] = useState(null);

  const carica = useCallback(async () => {
    try {
      const g = await gareApi.get(id);
      setGara(g);
      setTestata({
        nome: g.nome || "",
        oggetto: g.oggetto || "",
        note: g.note || "",
        scadenzaOfferte: dataInput(g.scadenzaOfferte),
      });
      setRsaIds((g.rsa || []).map((r) => r.id));
      setEmail(
        Object.fromEntries((g.inviti || []).map((i) => [i.id, i.email || ""])),
      );
      setErrore(null);
    } catch (e) {
      setErrore(e.message);
    } finally {
      setCaricamento(false);
    }
  }, [id]);

  useEffect(() => {
    carica();
    anagraficheApi
      .rsa()
      .then((r) => setRsa(r.value || []))
      .catch(() => {});
    fornitoriApi
      .list()
      .then((r) => setFornitori(r.value || []))
      .catch(() => {});
  }, [carica]);

  if (caricamento) return <Caricamento testo="Caricamento gara…" />;
  if (!gara)
    return <Avviso tipo="errore">{errore || "Gara non trovata"}</Avviso>;

  const inBozza = gara.stato === GARA_BOZZA;
  const finale =
    gara.stato === GARA_AGGIUDICATA || gara.stato === GARA_ANNULLATA;
  const scrivibile = puoScrivere && !finale;

  const azione = async (fn, messaggio, { ricarica = true } = {}) => {
    setInCorso(true);
    setErrore(null);
    try {
      const res = await fn();
      if (messaggio)
        showToast(
          typeof messaggio === "function" ? messaggio(res) : messaggio,
          "success",
        );
      if (ricarica) await carica();
      return res;
    } catch (e) {
      const problemi = e.data?.problemi;
      setErrore(
        problemi?.length
          ? `${e.data.message}`
          : e.data?.message || e.message || "Operazione fallita",
      );
      return null;
    } finally {
      setInCorso(false);
    }
  };

  const salvaTestata = (e) => {
    e.preventDefault();
    const body = inBozza
      ? { ...testata, rsaIds }
      : { note: testata.note, scadenzaOfferte: testata.scadenzaOfferte };
    azione(() => gareApi.update(id, body), "Gara aggiornata");
  };

  const invia = async () => {
    const n = gara.inviti.filter((i) => i.stato === INVITO_DA_INVITARE).length;
    if (
      !window.confirm(
        `Inviare la richiesta di offerta a ${n} fornitori?\n\n` +
          "Il capitolato verrà bloccato, i fornitori passeranno a «In trattativa» " +
          "(anonimi nel portale manutenzioni) e partiranno le email con il PDF.",
      )
    )
      return;
    const res = await azione(
      () => gareApi.invia(id),
      (r) => r.message,
    );
    if (res) setEsitiInvio(res.esiti || []);
  };

  const annulla = () => {
    const motivo = window.prompt("Motivo dell'annullamento (obbligatorio):");
    if (!motivo?.trim()) return;
    azione(() => gareApi.annulla(id, motivo.trim()), "Gara annullata");
  };

  const elimina = async () => {
    if (!window.confirm("Eliminare questa gara in bozza e i suoi invitati?"))
      return;
    const ok = await azione(() => gareApi.remove(id), "Gara eliminata", {
      ricarica: false,
    });
    if (ok) navigate("/gare");
  };

  const registraOfferta = async (invitoId) => {
    const res = await azione(
      () => gareApi.creaOfferta(id, { invitoId }),
      "Offerta creata",
      {
        ricarica: false,
      },
    );
    if (res) navigate(`/gare/${id}/offerte/${res.id}`);
  };

  const toggleRsa = (rid) =>
    setRsaIds((l) =>
      l.includes(rid) ? l.filter((x) => x !== rid) : [...l, rid],
    );

  const invitati = new Set(
    gara.inviti.map((i) => i.fornitore?.id?.toLowerCase()),
  );
  const aggiungibili = fornitori.filter(
    (f) => !invitati.has(String(f.id).toLowerCase()),
  );
  const offertaPerInvito = new Map();
  for (const o of gara.offerte) {
    if (o.invitoId && !offertaPerInvito.has(o.invitoId))
      offertaPerInvito.set(o.invitoId, o);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/gare" className="text-sm text-acquisti-700 hover:underline">
          ← Gare
        </Link>
        <h1 className="text-xl font-semibold text-gray-900">{gara.nome}</h1>
        <span className="font-mono text-xs text-gray-500">{gara.codice}</span>
        <span className={STATO_GARA_BADGE[gara.stato] || "badge-gray"}>
          {gara.statoLabel || etichetta(STATO_GARA, gara.stato)}
        </span>
        <div className="ml-auto flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() =>
              apriFile(gareApi.richiestaPdf(id)).catch((e) =>
                setErrore(e.message),
              )
            }
          >
            {gara.richiestaInviata
              ? "Richiesta inviata (PDF)"
              : "Anteprima PDF"}
          </button>
          {(gara.stato === GARA_IN_VALUTAZIONE ||
            gara.stato === GARA_AGGIUDICATA) && (
            <Link to={`/gare/${id}/comparazione`} className="btn-secondary">
              Comparazione
            </Link>
          )}
          {scrivibile && inBozza && (
            <>
              <button
                type="button"
                className="btn-primary"
                disabled={inCorso}
                onClick={invia}
              >
                Invia ai fornitori
              </button>
              <button
                type="button"
                className="btn-danger"
                disabled={inCorso}
                onClick={elimina}
              >
                Elimina
              </button>
            </>
          )}
          {scrivibile && gara.stato === GARA_INVIATA && (
            <button
              type="button"
              className="btn-primary"
              disabled={inCorso}
              onClick={() =>
                azione(
                  () => gareApi.chiudiRaccolta(id),
                  "Raccolta offerte chiusa",
                )
              }
            >
              Chiudi raccolta offerte
            </button>
          )}
          {scrivibile && gara.stato === GARA_IN_VALUTAZIONE && (
            <button
              type="button"
              className="btn-secondary"
              disabled={inCorso}
              onClick={() =>
                azione(
                  () => gareApi.riapriRaccolta(id),
                  "Raccolta offerte riaperta",
                )
              }
            >
              Riapri raccolta
            </button>
          )}
          {scrivibile && !inBozza && (
            <button
              type="button"
              className="btn-danger"
              disabled={inCorso}
              onClick={annulla}
            >
              Annulla gara
            </button>
          )}
        </div>
      </div>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {gara.stato === GARA_ANNULLATA && (
        <Avviso tipo="attenzione">
          Gara annullata: {gara.motivoAnnullamento || "motivo non indicato"}. I
          fornitori portati a «In trattativa» restano tali finché l'Ufficio
          Acquisti non li riporta a «Condiviso» da Fornitori.
        </Avviso>
      )}
      {esitiInvio && esitiInvio.some((e) => !e.ok) && (
        <Avviso tipo="errore">
          Inviti non partiti:{" "}
          {esitiInvio
            .filter((e) => !e.ok)
            .map((e) => `${e.fornitore} (${e.errore})`)
            .join("; ")}
          . Correggi l'indirizzo e ritenta dalla riga dell'invitato.
        </Avviso>
      )}

      <form onSubmit={salvaTestata} className="card space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Richiesta</h2>
          {scrivibile && (
            <button type="submit" className="btn-primary" disabled={inCorso}>
              Salva
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <p>
            Capitolato:{" "}
            {gara.capitolato ? (
              <Link
                to={`/capitolati/${gara.capitolato.id}`}
                className="text-acquisti-700 hover:underline"
              >
                {gara.capitolato.nome}
              </Link>
            ) : (
              <strong className="text-red-700">
                nessuno — serve per inviare
              </strong>
            )}
            {gara.capitolato?.bloccato && (
              <span className="badge-danger ml-2">bloccato</span>
            )}
          </p>
          <p>Tipologia: {gara.tipologiaFornitura?.nome || "—"}</p>
          <p>
            Contratto uscente:{" "}
            {gara.contrattoUscente ? (
              <Link
                to={`/contratti/${gara.contrattoUscente.id}`}
                className="text-acquisti-700 hover:underline"
              >
                {gara.contrattoUscente.nome}
              </Link>
            ) : (
              "—"
            )}
          </p>
          <p>
            Origine: {etichetta(ORIGINE_GARA, gara.origine)} · Inviata il{" "}
            {gara.dataInvio
              ? new Date(gara.dataInvio).toLocaleDateString("it-IT")
              : "—"}
          </p>
        </div>
        <fieldset
          disabled={!scrivibile}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <Campo label="Nome">
            <Testo
              valore={testata.nome}
              onChange={(v) => setTestata((t) => ({ ...t, nome: v }))}
              disabled={!inBozza}
            />
          </Campo>
          <Campo label="Scadenza offerte">
            <Data
              valore={testata.scadenzaOfferte}
              onChange={(v) =>
                setTestata((t) => ({ ...t, scadenzaOfferte: v }))
              }
            />
          </Campo>
          <Campo
            label="Descrizione della fornitura"
            aiuto="Esce nel PDF ai fornitori."
            className="md:col-span-2"
          >
            <AreaTesto
              valore={testata.oggetto}
              onChange={(v) => setTestata((t) => ({ ...t, oggetto: v }))}
              righe={4}
              disabled={!inBozza}
            />
          </Campo>
          <Campo
            label="Note interne"
            aiuto="Non escono nel PDF."
            className="md:col-span-2"
          >
            <AreaTesto
              valore={testata.note}
              onChange={(v) => setTestata((t) => ({ ...t, note: v }))}
            />
          </Campo>
        </fieldset>
        <div>
          <span className="label">RSA interessate</span>
          {inBozza && scrivibile ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-1">
              {rsa.map((r) => (
                <label
                  key={r.id}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-acquisti-600 focus:ring-acquisti-500"
                    checked={rsaIds.includes(r.id)}
                    onChange={() => toggleRsa(r.id)}
                  />
                  {r.nome}
                </label>
              ))}
            </div>
          ) : (
            <p className="text-sm">
              {gara.rsa.map((r) => r.nome).join(", ") || "—"}
            </p>
          )}
        </div>
      </form>

      <section className="card !p-0 overflow-x-auto">
        <div className="flex flex-wrap items-center gap-3 p-4">
          <h2 className="font-semibold text-gray-900">
            Invitati ({gara.inviti.length})
          </h2>
          {scrivibile && (inBozza || gara.stato === GARA_INVIATA) && (
            <div className="ml-auto flex gap-2 items-center">
              <Scelta
                valore={nuovoFornitore}
                onChange={setNuovoFornitore}
                opzioni={aggiungibili.map((f) => ({
                  value: f.id,
                  label: f.nome,
                }))}
                vuoto="aggiungi un fornitore…"
              />
              <button
                type="button"
                className="btn-secondary whitespace-nowrap"
                disabled={!nuovoFornitore || inCorso}
                onClick={async () => {
                  const ok = await azione(
                    () =>
                      gareApi.aggiungiInvito(id, {
                        fornitoreId: nuovoFornitore,
                      }),
                    "Fornitore aggiunto",
                  );
                  if (ok) setNuovoFornitore("");
                }}
              >
                Aggiungi
              </button>
            </div>
          )}
        </div>
        {gara.inviti.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-gray-500">
            Nessun invitato: l'albo non ha fornitori che coprano tutte le tipologie della gara.
            Aggiungili qui sopra.
          </p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Fornitore</th>
                <th>Email di invio</th>
                <th>Stato</th>
                <th>Offerta</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {gara.inviti.map((i) => {
                const nonPartito =
                  i.stato === INVITO_DA_INVITARE || i.stato === INVITO_FALLITO;
                const offerta = offertaPerInvito.get(i.id);
                return (
                  <tr key={i.id}>
                    <td>
                      <div className="font-medium">
                        {i.fornitore?.nome || "—"}
                      </div>
                      <div className="text-xs text-gray-500 space-x-1">
                        {i.proposto ? (
                          <span>dall'albo</span>
                        ) : (
                          <span>aggiunto a mano</span>
                        )}
                        {i.fornitore?.livello != null && (
                          <span className={LIVELLO_BADGE[i.fornitore.livello]}>
                            {LIVELLO_RISERVATEZZA[i.fornitore.livello]}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="min-w-[14rem]">
                      {nonPartito && scrivibile ? (
                        <div className="flex gap-1">
                          <input
                            type="email"
                            className="input"
                            value={email[i.id] ?? ""}
                            onChange={(e) =>
                              setEmail((m) => ({
                                ...m,
                                [i.id]: e.target.value,
                              }))
                            }
                            placeholder="indirizzo mancante"
                          />
                          {(email[i.id] ?? "") !== (i.email ?? "") && (
                            <button
                              type="button"
                              className="btn-secondary text-xs"
                              onClick={() =>
                                azione(
                                  () =>
                                    gareApi.aggiornaInvito(id, i.id, {
                                      email: email[i.id],
                                    }),
                                  "Email aggiornata",
                                )
                              }
                            >
                              Salva
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm">{i.email || "—"}</span>
                      )}
                      {i.esitoInvio && (
                        <div
                          className={`text-xs mt-1 ${i.stato === INVITO_FALLITO ? "text-red-700" : "text-gray-500"}`}
                        >
                          {i.esitoInvio}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      <span
                        className={STATO_INVITO_BADGE[i.stato] || "badge-gray"}
                      >
                        {i.statoLabel || etichetta(STATO_INVITO, i.stato)}
                      </span>
                      {i.dataInvito && (
                        <div className="text-xs text-gray-500">
                          {new Date(i.dataInvito).toLocaleDateString("it-IT")}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap text-sm">
                      {offerta ? (
                        <Link
                          to={`/gare/${id}/offerte/${offerta.id}`}
                          className="text-acquisti-700 hover:underline"
                        >
                          rev. {offerta.revisione} ·{" "}
                          {euro(offerta.importoTotale)}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="whitespace-nowrap text-right space-x-1">
                      {scrivibile && nonPartito && inBozza && (
                        <button
                          type="button"
                          className="btn-secondary text-xs"
                          onClick={() =>
                            azione(
                              () => gareApi.rimuoviInvito(id, i.id),
                              "Invitato rimosso",
                            )
                          }
                        >
                          Rimuovi
                        </button>
                      )}
                      {scrivibile &&
                        nonPartito &&
                        gara.stato === GARA_INVIATA && (
                          <button
                            type="button"
                            className="btn-primary text-xs"
                            disabled={inCorso}
                            onClick={() =>
                              azione(
                                () => gareApi.inviaInvito(id, i.id),
                                (r) => r.message,
                              )
                            }
                          >
                            Invia
                          </button>
                        )}
                      {scrivibile &&
                        gara.offerteModificabili &&
                        !offerta &&
                        (i.stato === INVITO_INVITATO ||
                          i.stato === INVITO_HA_RISPOSTO) && (
                          <>
                            <button
                              type="button"
                              className="btn-primary text-xs"
                              disabled={inCorso}
                              onClick={() => registraOfferta(i.id)}
                            >
                              Registra offerta
                            </button>
                            <button
                              type="button"
                              className="btn-secondary text-xs"
                              onClick={() =>
                                azione(
                                  () => gareApi.declinaInvito(id, i.id),
                                  "Segnato come declinato",
                                )
                              }
                            >
                              Ha declinato
                            </button>
                          </>
                        )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {gara.offerte.length > 0 && (
        <section className="card !p-0 overflow-x-auto">
          <h2 className="font-semibold text-gray-900 p-4">
            Offerte e revisioni
          </h2>
          <table className="table">
            <thead>
              <tr>
                <th>Fornitore</th>
                <th className="text-center">Rev.</th>
                <th>Stato</th>
                <th>Ricevuta</th>
                <th className="text-right">Importo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {gara.offerte.map((o) => (
                <tr
                  key={o.id}
                  className={
                    o.stato === OFFERTA_SUPERATA || o.stato === OFFERTA_RITIRATA
                      ? "text-gray-400"
                      : ""
                  }
                >
                  <td>
                    <Link
                      to={`/gare/${id}/offerte/${o.id}`}
                      className="text-acquisti-700 hover:underline"
                    >
                      {o.fornitore?.nome || o.nome}
                    </Link>
                  </td>
                  <td className="text-center">{o.revisione}</td>
                  <td>
                    <span
                      className={STATO_OFFERTA_BADGE[o.stato] || "badge-gray"}
                    >
                      {o.statoLabel || etichetta(STATO_OFFERTA, o.stato)}
                    </span>
                  </td>
                  <td>{dataIt(o.dataRicezione)}</td>
                  <td className="text-right">{euro(o.importoTotale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className="card text-sm text-gray-600">
        Creata da <strong>{gara.creataDa || "sistema"}</strong>
        {gara.modificataDa && (
          <>
            {" "}
            · ultima modifica <strong>{gara.modificataDa}</strong>
          </>
        )}
      </section>
    </div>
  );
}
